import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Chip,
  OutlinedInput,
  ListItemText,
} from '@mui/material';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { FormSection } from '../../types/questionnaire.types';
import { questionnaireService } from '../../services/questionnaireService';
import { patientService } from '../../services/patientService';
import { useTenant } from '../../context/TenantContext';

interface SectionEditDialogProps {
  open: boolean;
  section?: FormSection;
  questionnaireType?: 'create_patient' | 'collect_info' | 'il_webhook' | 'create_lead';
  onSave: (section: FormSection) => void;
  onCancel: () => void;
}

const inputLabelProps = { shrink: true, sx: { color: POPUP_COLORS.titleText } };
const inputProps = { style: CLINIC_DIALOG_SX.textField as React.CSSProperties };

export const SectionEditDialog: React.FC<SectionEditDialogProps> = ({
  open,
  section,
  questionnaireType,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleAlignment, setTitleAlignment] = useState<'left' | 'center'>('center');
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [progressBarText, setProgressBarText] = useState('');
  const [progressBarLabel, setProgressBarLabel] = useState('');
  const [showBackButton, setShowBackButton] = useState(false);
  const [scoreGroup, setScoreGroup] = useState('');
  const [showBasedOnScore, setShowBasedOnScore] = useState(false);
  const [scoreNameToCheck, setScoreNameToCheck] = useState('');
  const [scoreValueToMatch, setScoreValueToMatch] = useState<'High' | 'Moderate' | 'Low' | ''>('');
  const [showBasedOnLead, setShowBasedOnLead] = useState(false);
  const [leadCondition, setLeadCondition] = useState<'exists' | 'not_exists'>('exists');
  const [alertBannerEnabled, setAlertBannerEnabled] = useState(false);
  const [alertBannerType, setAlertBannerType] = useState<'warning' | 'info' | 'danger'>('warning');
  const [alertBannerContent, setAlertBannerContent] = useState('');
  const [validations, setValidations] = useState<string[]>([]);
  const [availableValidators, setAvailableValidators] = useState<string[]>([]);
  const [updatePatientFieldEnabled, setUpdatePatientFieldEnabled] = useState(false);
  const [updatePatientFieldName, setUpdatePatientFieldName] = useState('');
  const [updatePatientFieldValue, setUpdatePatientFieldValue] = useState('');
  const [availablePatientFields, setAvailablePatientFields] = useState<
    Array<{ name: string; label: string; options?: Array<{ value: string; label: string }> }>
  >([]);

  const { currentTenant } = useTenant();
  const isCollectInfo = questionnaireType === 'collect_info';
  const selectedPatientField = availablePatientFields.find((f) => f.name === updatePatientFieldName);
  const patientFieldOptions = selectedPatientField?.options || [];

  useEffect(() => {
    if (open) {
      questionnaireService.getSectionValidators()
        .then(setAvailableValidators)
        .catch(() => setAvailableValidators([]));
      if (isCollectInfo) {
        patientService.getPatientFields(undefined, currentTenant?.id)
          .then((fields) => setAvailablePatientFields(
            fields.map((f) => ({ name: f.name, label: f.label, options: f.options }))
          ))
          .catch(() => setAvailablePatientFields([]));
      }
      if (section) {
        setTitle(section.title);
        setDescription(section.description || '');
        setTitleAlignment(section.titleAlignment || 'center');
        setShowProgressBar(section.showProgressBar || false);
        setProgressBarText(section.progressBarText || '');
        setProgressBarLabel(section.progressBarLabel || '');
        setShowBackButton(section.showBackButton || false);
        setScoreGroup(section.scoreGroup || '');
        setShowBasedOnScore(section.showBasedOnScore || false);
        setScoreNameToCheck(section.scoreNameToCheck || '');
        setScoreValueToMatch(section.scoreValueToMatch || '');
        setShowBasedOnLead(section.showBasedOnLead || false);
        setLeadCondition(section.leadCondition || 'exists');
        setAlertBannerEnabled(!!section.alertBanner);
        setAlertBannerType(section.alertBanner?.type || 'warning');
        setAlertBannerContent(section.alertBanner?.content || '');
        setValidations(section.validations || []);
        setUpdatePatientFieldEnabled(!!section.updatePatientField?.enabled);
        setUpdatePatientFieldName(section.updatePatientField?.fieldName || '');
        setUpdatePatientFieldValue(section.updatePatientField?.value || '');
      } else {
        setTitle('');
        setDescription('');
        setTitleAlignment('center');
        setShowProgressBar(false);
        setProgressBarText('');
        setProgressBarLabel('');
        setShowBackButton(false);
        setScoreGroup('');
        setShowBasedOnScore(false);
        setScoreNameToCheck('');
        setScoreValueToMatch('');
        setShowBasedOnLead(false);
        setLeadCondition('exists');
        setAlertBannerEnabled(false);
        setAlertBannerType('warning');
        setAlertBannerContent('');
        setValidations([]);
        setUpdatePatientFieldEnabled(false);
        setUpdatePatientFieldName('');
        setUpdatePatientFieldValue('');
      }
    }
  }, [open, section, isCollectInfo, currentTenant?.id]);

  const handleSave = () => {
    if (!title.trim()) return;

    const sectionToSave = section || {
      id: `section_${Date.now()}`,
      title: '',
      description: '',
      order: 0,
      fields: [],
    };

    onSave({
      ...sectionToSave,
      title: title.trim(),
      description: description.trim(),
      titleAlignment,
      showProgressBar,
      progressBarText: progressBarText.trim(),
      progressBarLabel: progressBarLabel.trim(),
      scoreGroup: scoreGroup.trim() || undefined,
      showBackButton,
      showBasedOnScore,
      scoreNameToCheck: scoreNameToCheck.trim() || undefined,
      scoreValueToMatch: scoreValueToMatch || undefined,
      showBasedOnLead,
      leadCondition: showBasedOnLead ? leadCondition : undefined,
      alertBanner: alertBannerEnabled && alertBannerContent.trim()
        ? { type: alertBannerType, content: alertBannerContent.trim() }
        : undefined,
      validations: validations.length > 0 ? validations : undefined,
      updatePatientField:
        isCollectInfo && updatePatientFieldEnabled && updatePatientFieldName
          ? { enabled: true, fieldName: updatePatientFieldName, value: updatePatientFieldValue }
          : undefined,
    });
  };

  const checkboxSx = {
    color: POPUP_COLORS.titleText,
    '&.Mui-checked': { color: POPUP_COLORS.titleText },
  };

  const isDisabled =
    !title.trim() ||
    (showBasedOnScore && (!scoreNameToCheck.trim() || !scoreValueToMatch)) ||
    (alertBannerEnabled && !alertBannerContent.trim()) ||
    (isCollectInfo && updatePatientFieldEnabled && !updatePatientFieldName);

  const actions = (
    <>
      <Button onClick={onCancel} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
      <Button variant="contained" onClick={handleSave} disabled={isDisabled} sx={CLINIC_DIALOG_SX.primaryButton}>
        Save Section
      </Button>
    </>
  );

  return (
    <ClinicDialog
      open={open}
      onClose={onCancel}
      title={section ? 'Edit Section' : 'Create Section'}
      maxWidth="md"
      actions={actions}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '4px 0' }}>
        <TextField
          sx={{ mt: 1 }}
          fullWidth
          label="Section Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Personal Information"
          autoFocus
          variant="outlined"
          InputLabelProps={inputLabelProps}
          InputProps={inputProps}
        />
        <TextField
          fullWidth
          label="Description (Optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Enter section description..."
          multiline
          rows={3}
          variant="outlined"
          InputLabelProps={inputLabelProps}
          InputProps={inputProps}
        />

        {/* Alert Banner */}
        <FormControlLabel
          control={
            <Checkbox checked={alertBannerEnabled} onChange={(e) => setAlertBannerEnabled(e.target.checked)} sx={checkboxSx} />
          }
          label="Show Alert Banner"
          sx={{ color: POPUP_COLORS.titleText }}
        />
        {alertBannerEnabled && (
          <>
            <FormControl fullWidth variant="outlined">
              <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Alert Type</InputLabel>
              <Select
                value={alertBannerType}
                onChange={(e) => setAlertBannerType(e.target.value as 'warning' | 'info' | 'danger')}
                label="Alert Type"
                sx={CLINIC_DIALOG_SX.select}
              >
                <MenuItem value="warning">Warning (Teal)</MenuItem>
                <MenuItem value="info">Info (Light Blue)</MenuItem>
                <MenuItem value="danger">Danger (Red)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              required
              label="Alert Content"
              value={alertBannerContent}
              onChange={(e) => setAlertBannerContent(e.target.value)}
              placeholder="e.g., If you are in immediate danger, contact emergency services."
              multiline
              rows={3}
              variant="outlined"
              helperText="This alert will appear below the section description"
              InputLabelProps={inputLabelProps}
              InputProps={inputProps}
              error={alertBannerEnabled && !alertBannerContent.trim()}
            />
          </>
        )}

        <FormControl fullWidth variant="outlined">
          <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Title Alignment</InputLabel>
          <Select
            value={titleAlignment}
            onChange={(e) => setTitleAlignment(e.target.value as 'left' | 'center')}
            label="Title Alignment"
            sx={CLINIC_DIALOG_SX.select}
          >
            <MenuItem value="center">Center</MenuItem>
            <MenuItem value="left">Left</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Progress Bar Text"
          value={progressBarText}
          onChange={(e) => setProgressBarText(e.target.value)}
          placeholder="e.g., 1, 2, 3"
          variant="outlined"
          InputLabelProps={inputLabelProps}
          InputProps={inputProps}
        />
        <TextField
          fullWidth
          label="Progress Bar Label"
          value={progressBarLabel}
          onChange={(e) => setProgressBarLabel(e.target.value)}
          placeholder="e.g., Screening, ASRS, Results"
          variant="outlined"
          helperText="Label shown below the progress bar circle (15px font size)"
          InputLabelProps={inputLabelProps}
          InputProps={inputProps}
        />

        <FormControlLabel
          control={
            <Checkbox checked={showProgressBar} onChange={(e) => setShowProgressBar(e.target.checked)} sx={checkboxSx} />
          }
          label="Show Progress Bar"
          sx={{ color: POPUP_COLORS.titleText }}
        />
        <FormControlLabel
          control={
            <Checkbox checked={showBackButton} onChange={(e) => setShowBackButton(e.target.checked)} sx={checkboxSx} />
          }
          label="Show Back Button"
          sx={{ color: POPUP_COLORS.titleText }}
        />

        <TextField
          fullWidth
          label="Score Group (Optional)"
          value={scoreGroup}
          onChange={(e) => setScoreGroup(e.target.value)}
          placeholder="e.g., anxiety, depression"
          variant="outlined"
          helperText="All fields in this section will automatically belong to this score group"
          InputLabelProps={inputLabelProps}
          InputProps={inputProps}
        />

        <FormControl fullWidth variant="outlined">
          <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Section Validations (Optional)</InputLabel>
          <Select
            multiple
            value={validations}
            onChange={(e) => {
              const value = e.target.value;
              setValidations(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput notched label="Section Validations (Optional)" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
            sx={CLINIC_DIALOG_SX.select}
          >
            {availableValidators.length === 0 && (
              <MenuItem disabled>No validators available</MenuItem>
            )}
            {availableValidators.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={validations.includes(name)} sx={checkboxSx} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox checked={showBasedOnScore} onChange={(e) => setShowBasedOnScore(e.target.checked)} sx={checkboxSx} />
          }
          label="Show Section based on User Score"
          sx={{ color: POPUP_COLORS.titleText }}
        />
        {showBasedOnScore && (
          <>
            <TextField
              fullWidth
              required
              label="Score Name"
              value={scoreNameToCheck}
              onChange={(e) => setScoreNameToCheck(e.target.value)}
              placeholder="e.g., ASRS, PHQ-9, GAD-7"
              variant="outlined"
              helperText="The score name to check for this section's visibility"
              InputLabelProps={inputLabelProps}
              InputProps={inputProps}
              error={showBasedOnScore && !scoreNameToCheck.trim()}
            />
            <FormControl fullWidth required error={showBasedOnScore && !scoreValueToMatch}>
              <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Score Value</InputLabel>
              <Select
                value={scoreValueToMatch}
                label="Score Value"
                onChange={(e) => setScoreValueToMatch(e.target.value as 'High' | 'Moderate' | 'Low')}
                sx={CLINIC_DIALOG_SX.select}
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Moderate">Moderate</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </Select>
            </FormControl>
          </>
        )}

        <FormControlLabel
          control={
            <Checkbox checked={showBasedOnLead} onChange={(e) => setShowBasedOnLead(e.target.checked)} sx={checkboxSx} />
          }
          label="Show Section based on Lead"
          sx={{ color: POPUP_COLORS.titleText }}
        />
        {showBasedOnLead && (
          <FormControl fullWidth>
            <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Lead condition</InputLabel>
            <Select
              value={leadCondition}
              label="Lead condition"
              onChange={(e) => setLeadCondition(e.target.value as 'exists' | 'not_exists')}
              sx={CLINIC_DIALOG_SX.select}
            >
              <MenuItem value="exists">Show when a lead exists</MenuItem>
              <MenuItem value="not_exists">Show when no lead exists</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Update patient field when section is completed (collect_info only) */}
        {isCollectInfo && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={updatePatientFieldEnabled}
                  onChange={(e) => setUpdatePatientFieldEnabled(e.target.checked)}
                  sx={checkboxSx}
                />
              }
              label="Update patient field when section is completed (valid)"
              sx={{ color: POPUP_COLORS.titleText }}
            />
            {updatePatientFieldEnabled && (
              <>
                <FormControl fullWidth required variant="outlined" error={!updatePatientFieldName}>
                  <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Patient Field</InputLabel>
                  <Select
                    value={updatePatientFieldName}
                    label="Patient Field"
                    onChange={(e) => setUpdatePatientFieldName(e.target.value)}
                    input={<OutlinedInput notched label="Patient Field" />}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                    sx={CLINIC_DIALOG_SX.select}
                  >
                    {availablePatientFields.length === 0 && (
                      <MenuItem disabled>No patient fields available</MenuItem>
                    )}
                    {availablePatientFields.map((f) => (
                      <MenuItem key={f.name} value={f.name}>{f.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {patientFieldOptions.length > 0 ? (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Value</InputLabel>
                    <Select
                      value={updatePatientFieldValue}
                      label="Value"
                      onChange={(e) => setUpdatePatientFieldValue(e.target.value)}
                      input={<OutlinedInput notched label="Value" />}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                      sx={CLINIC_DIALOG_SX.select}
                    >
                      {patientFieldOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label="Value"
                    value={updatePatientFieldValue}
                    onChange={(e) => setUpdatePatientFieldValue(e.target.value)}
                    placeholder="Static value to write to the patient field"
                    variant="outlined"
                    helperText="This value is written to the selected patient field when the patient completes this section"
                    InputLabelProps={inputLabelProps}
                    InputProps={inputProps}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </ClinicDialog>
  );
};
